import { memo, useState, useEffect, useCallback } from 'react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useFormik } from 'formik';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useToaster from 'hooks/UseToaster';
//Import Components
import { Alert, Button, ExtendedInput, SkeletonLoader } from '@geneui/components';
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
import ResetSave from 'components/common/ResetSave';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Services
import { ProviderHttpService } from 'services/http';
// Import Constants
import { l, AlertTypes, CommonIcons, ModalsClassNames } from 'constants/common';

const { success } = AlertTypes;

const WhatsAppSettingsSection = () => {
    const { t } = useTranslation();

    const { doPostRequest } = useRequest();
    const { showToaster } = useToaster();

    const [isWhatsAppSettingsLoading, setIsWhatsAppSettingsLoading] = useState(false);
    const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
    const [isDisconnectModalLoading, setIsDisconnectModalLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            accessToken: '',
            wabaId: '',
            phoneNumberId: '',
            appId: '',
        },
        validationSchema: Yup.object({
            accessToken: Yup.string().required(t(l.CannotBeEmpty, { fieldName: t(l.AccessToken) })),
            wabaId: Yup.string().required(t(l.CannotBeEmpty, { fieldName: t(l.WaBAId) })),
            phoneNumberId: Yup.string().required(t(l.CannotBeEmpty, { fieldName: t(l.PhoneNumberId) })),
            appId: Yup.string().required(t(l.CannotBeEmpty, { fieldName: t(l.AppId) })),
        }),
        onSubmit: (values, formikHelpers) => {
            return doPostRequest(saveWhatsAppRequest.request, {
                requestBody: {
                    AccessToken: values.accessToken,
                    WabaId: values.wabaId,
                    PhoneNumberId: values.phoneNumberId,
                    AppId: values.appId,
                },
                successCallback: () => {
                    formikHelpers.resetForm({
                        values: {
                            accessToken: values.accessToken,
                            wabaId: values.wabaId,
                            phoneNumberId: values.phoneNumberId,
                            appId: values.appId,
                        },
                    });
                    showToaster(success, t(l.WhatsAppSettingsSavedSuccessfully));
                },
            });
        },
    });

    const { values, errors, dirty, isSubmitting, isValid, setFieldValue, submitForm, resetForm } = formik;

    const { getWhatsAppRequest, saveWhatsAppRequest, deleteWhatsAppRequest } = useCancelableRequests({
        getWhatsAppRequest: ProviderHttpService.getWhatsApp,
        saveWhatsAppRequest: ProviderHttpService.saveWhatsApp,
        deleteWhatsAppRequest: ProviderHttpService.deleteWhatsApp,
    });

    const getWhatsAppSettings = () => {
        setIsWhatsAppSettingsLoading(true);
        doPostRequest(getWhatsAppRequest.request, {
            requestBody: {},
            successCallback: (data) => {
                resetForm({
                    values: {
                        accessToken: data?.AccessToken ?? '',
                        wabaId: data?.WabaId ?? '',
                        phoneNumberId: data?.PhoneNumberId ?? '',
                        appId: data?.AppId ?? '',
                    },
                });
            },
        }).then(() => {
            setIsWhatsAppSettingsLoading(false);
        });
    };

    const onDisconnect = useCallback(() => setIsDisconnectModalOpen(true), []);
    const onCancelDisconnectModal = useCallback(() => setIsDisconnectModalOpen(false), []);

    const removeSettings = useCallback(() => {
        setIsDisconnectModalLoading(true);

        doPostRequest(deleteWhatsAppRequest.request, {
            requestBody: {},
            successCallback: () => {
                resetForm({
                    values: {
                        accessToken: '',
                        wabaId: '',
                        phoneNumberId: '',
                        appId: '',
                    },
                });
                setIsDisconnectModalOpen(false);

                showToaster(success, t(l.WhatsAppSettingsDisconnectedSuccessfully));
            },
        }).then(() => {
            setIsDisconnectModalLoading(false);
        });
    }, [doPostRequest, deleteWhatsAppRequest.request, resetForm, showToaster, t]);

    const isAllFieldsEmpty =
        isEmpty(values.accessToken) && isEmpty(values.wabaId) && isEmpty(values.phoneNumberId) && isEmpty(values.appId);

    const init = () => {
        getWhatsAppSettings();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <>
            <SectionWithTitle
                title={t(l.WhatsAppSettings)}
                className="crm-whatsapp-provider-settings-wrapper"
                contentClassName="crm-whatsapp-provider-settings-content"
            >
                <Alert closable={false} title={t(l.WhatsAppSettingsDescription)} iconProps={{ type: 'bc-icon-info' }} />
                <div className="fields-btn-group">
                    <div className="input-container">
                        <SkeletonLoader isBusy={isWhatsAppSettingsLoading}>
                            <ExtendedInput
                                value={values.accessToken}
                                onChange={(e) => setFieldValue('accessToken', e.target.value)}
                                disabled={isSubmitting}
                                placeholder={t(l.AccessToken)}
                                labelAppearance="swap"
                                appearance="outline"
                                isValid={!errors.accessToken}
                                errorText={errors.accessToken}
                                type="password"
                            />
                        </SkeletonLoader>
                    </div>
                    <div className="input-container">
                        <SkeletonLoader isBusy={isWhatsAppSettingsLoading}>
                            <ExtendedInput
                                value={values.wabaId}
                                onChange={(e) => setFieldValue('wabaId', e.target.value)}
                                disabled={isSubmitting}
                                placeholder={t(l.WhatsAppBusinessAccountId)}
                                labelAppearance="swap"
                                appearance="outline"
                                isValid={!errors.wabaId}
                                errorText={errors.wabaId}
                                type="text"
                            />
                        </SkeletonLoader>
                    </div>
                    <div className="input-container">
                        <SkeletonLoader isBusy={isWhatsAppSettingsLoading}>
                            <ExtendedInput
                                value={values.phoneNumberId}
                                onChange={(e) => setFieldValue('phoneNumberId', e.target.value)}
                                disabled={isSubmitting}
                                placeholder={t(l.PhoneNumberId)}
                                labelAppearance="swap"
                                appearance="outline"
                                isValid={!errors.phoneNumberId}
                                errorText={errors.phoneNumberId}
                                type="text"
                            />
                        </SkeletonLoader>
                    </div>
                    <div className="input-container">
                        <SkeletonLoader isBusy={isWhatsAppSettingsLoading}>
                            <ExtendedInput
                                value={values.appId}
                                onChange={(e) => setFieldValue('appId', e.target.value)}
                                disabled={isSubmitting}
                                placeholder={t(l.AppId)}
                                labelAppearance="swap"
                                appearance="outline"
                                isValid={!errors.appId}
                                errorText={errors.appId}
                                type="text"
                            />
                        </SkeletonLoader>
                    </div>
                    <div className="whatsapp-settings-content-action-buttons-container">
                        <Button
                            appearance="outline"
                            onClick={onDisconnect}
                            disabled={isAllFieldsEmpty || isWhatsAppSettingsLoading}
                        >
                            {t(l.Disconnect)}
                        </Button>
                        <ResetSave
                            hasAdditionalSettings={false}
                            resetButtonClickHandler={resetForm}
                            disableSave={!isValid || !dirty || isSubmitting}
                            disableReset={!dirty || isSubmitting}
                            saveButtonClickHandler={submitForm}
                            isLoadingSave={isSubmitting}
                        />
                    </div>
                </div>
            </SectionWithTitle>
            {isDisconnectModalOpen && (
                <ConfirmationModal
                    onOk={removeSettings}
                    onCancel={onCancelDisconnectModal}
                    isVisibleModal={isDisconnectModalOpen}
                    titleText={t(l.DisconnectWhatsApp)}
                    questionLabel={t(l.DisconnectWhatsAppDescription)}
                    iconType={CommonIcons.Info}
                    actionLabel={t(l.Confirm)}
                    className={ModalsClassNames.UsedIn}
                    isLoading={isDisconnectModalLoading}
                />
            )}
        </>
    );
};

export default memo(WhatsAppSettingsSection);
